import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { HomeIcon, UsersIcon, ClipboardListIcon, TrendingUpIcon, CheckCircleIcon, XCircleIcon, ClockIcon, EyeIcon, ShieldIcon, DollarSignIcon, AlertCircleIcon } from 'lucide-react';
import { mockListings, mockUsers, formatCurrency, formatDate, licensePlans } from '../utils/mockData';
const AdminDashboardPage = () => {
  const [activeTab, setActiveTab] = useState<'overview' | 'listings' | 'users' | 'revenue'>('overview');
  const [selectedListing, setSelectedListing] = useState<string | null>(null);
  // Statistics
  const totalListings = mockListings.length;
  const pendingListings = mockListings.filter(l => l.status === 'pending').length;
  const approvedListings = mockListings.filter(l => l.status === 'approved').length;
  const rejectedListings = mockListings.filter(l => l.status === 'rejected').length;
  const totalUsers = mockUsers.filter(u => u.role !== 'admin').length;
  const activeUsers = mockUsers.filter(u => u.isActive && u.role !== 'admin').length;
  const usersWithLicense = mockUsers.filter(u => u.license && u.role !== 'admin').length;
  const totalRevenue = mockUsers.filter(u => u.license).reduce((sum, u) => {
    const plan = licensePlans.find(p => p.id === u.license?.licenseId);
    return sum + (plan?.price || 0);
  }, 0);
  const handleApproveListing = (listingId: string) => {
    console.log('Approve listing:', listingId);
    alert('Listing approved successfully!');
  };
  const handleRejectListing = (listingId: string) => {
    const reason = prompt('Enter rejection reason:');
    if (reason) {
      console.log('Reject listing:', listingId, 'Reason:', reason);
      alert('Listing rejected with reason provided to owner.');
    }
  };
  const handleToggleUserStatus = (userId: string) => {
    console.log('Toggle user status:', userId);
    alert('User status updated!');
  };
  return <div className="bg-gray-50 min-h-screen w-full" data-id="element-376">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-377">
        {/* Header */}
        <div className="mb-8" data-id="element-378">
          <div className="flex items-center mb-2" data-id="element-379">
            <ShieldIcon className="h-8 w-8 text-primary mr-3" data-id="element-380" />
            <h1 className="text-3xl font-bold text-gray-900" data-id="element-381">
              Admin Dashboard
            </h1>
          </div>
          <p className="text-gray-600" data-id="element-382">
            Manage listings, users, and platform operations
          </p>
        </div>

        {/* Navigation Tabs */}
        <div className="bg-white rounded-xl shadow-sm mb-6 overflow-hidden" data-id="element-383">
          <div className="flex border-b border-gray-200" data-id="element-384">
            <button onClick={() => setActiveTab('overview')} className={`flex-1 px-6 py-4 text-sm font-medium transition-colors relative ${activeTab === 'overview' ? 'text-primary bg-blue-50' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'}`} data-id="element-385">
              <div className="flex items-center justify-center" data-id="element-386">
                <TrendingUpIcon className="h-5 w-5 mr-2" data-id="element-387" />
                Overview
              </div>
              {activeTab === 'overview' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" data-id="element-388"></div>}
            </button>
            <button onClick={() => setActiveTab('listings')} className={`flex-1 px-6 py-4 text-sm font-medium transition-colors relative ${activeTab === 'listings' ? 'text-primary bg-blue-50' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'}`} data-id="element-389">
              <div className="flex items-center justify-center" data-id="element-390">
                <ClipboardListIcon className="h-5 w-5 mr-2" data-id="element-391" />
                Listings
                {pendingListings > 0 && <span className="ml-2 bg-yellow-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center" data-id="element-392">
                    {pendingListings}
                  </span>}
              </div>
              {activeTab === 'listings' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" data-id="element-393"></div>}
            </button>
            <button onClick={() => setActiveTab('users')} className={`flex-1 px-6 py-4 text-sm font-medium transition-colors relative ${activeTab === 'users' ? 'text-primary bg-blue-50' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'}`} data-id="element-394">
              <div className="flex items-center justify-center" data-id="element-395">
                <UsersIcon className="h-5 w-5 mr-2" data-id="element-396" />
                Users
              </div>
              {activeTab === 'users' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" data-id="element-397"></div>}
            </button>
            <button onClick={() => setActiveTab('revenue')} className={`flex-1 px-6 py-4 text-sm font-medium transition-colors relative ${activeTab === 'revenue' ? 'text-primary bg-blue-50' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'}`} data-id="element-398">
              <div className="flex items-center justify-center" data-id="element-399">
                <DollarSignIcon className="h-5 w-5 mr-2" data-id="element-400" />
                Revenue
              </div>
              {activeTab === 'revenue' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" data-id="element-401"></div>}
            </button>
          </div>
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && <div className="space-y-6" data-id="element-402">
            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-403">
              <div className="bg-white rounded-xl shadow-sm p-6" data-id="element-404">
                <div className="flex items-center justify-between mb-4" data-id="element-405">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center" data-id="element-406">
                    <ClipboardListIcon className="h-6 w-6 text-primary" data-id="element-407" />
                  </div>
                  <span className="text-xs font-medium text-gray-500 uppercase" data-id="element-408">
                    Total Listings
                  </span>
                </div>
                <div className="text-3xl font-bold text-gray-900" data-id="element-409">
                  {totalListings}
                </div>
                <div className="mt-2 flex items-center text-sm" data-id="element-410">
                  <span className="text-green-600 font-medium" data-id="element-411">
                    {approvedListings} approved
                  </span>
                  <span className="text-gray-400 mx-2" data-id="element-412">•</span>
                  <span className="text-yellow-600 font-medium" data-id="element-413">
                    {pendingListings} pending
                  </span>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-sm p-6" data-id="element-414">
                <div className="flex items-center justify-between mb-4" data-id="element-415">
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center" data-id="element-416">
                    <UsersIcon className="h-6 w-6 text-green-600" data-id="element-417" />
                  </div>
                  <span className="text-xs font-medium text-gray-500 uppercase" data-id="element-418">
                    Total Users
                  </span>
                </div>
                <div className="text-3xl font-bold text-gray-900" data-id="element-419">
                  {totalUsers}
                </div>
                <div className="mt-2 flex items-center text-sm" data-id="element-420">
                  <span className="text-green-600 font-medium" data-id="element-421">
                    {activeUsers} active
                  </span>
                  <span className="text-gray-400 mx-2" data-id="element-422">•</span>
                  <span className="text-gray-600" data-id="element-423">
                    {usersWithLicense} licensed
                  </span>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-sm p-6" data-id="element-424">
                <div className="flex items-center justify-between mb-4" data-id="element-425">
                  <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center" data-id="element-426">
                    <DollarSignIcon className="h-6 w-6 text-purple-600" data-id="element-427" />
                  </div>
                  <span className="text-xs font-medium text-gray-500 uppercase" data-id="element-428">
                    Revenue
                  </span>
                </div>
                <div className="text-3xl font-bold text-gray-900" data-id="element-429">
                  {formatCurrency(totalRevenue)}
                </div>
                <div className="mt-2 text-sm text-gray-600" data-id="element-430">
                  From {usersWithLicense} licenses
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-sm p-6" data-id="element-431">
                <div className="flex items-center justify-between mb-4" data-id="element-432">
                  <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center" data-id="element-433">
                    <ClockIcon className="h-6 w-6 text-yellow-600" data-id="element-434" />
                  </div>
                  <span className="text-xs font-medium text-gray-500 uppercase" data-id="element-435">
                    Pending Review
                  </span>
                </div>
                <div className="text-3xl font-bold text-gray-900" data-id="element-436">
                  {pendingListings}
                </div>
                <div className="mt-2 text-sm text-yellow-600 font-medium" data-id="element-437">
                  Requires attention
                </div>
              </div>
            </div>

            {/* Recent Activity */}
            <div className="bg-white rounded-xl shadow-sm p-6" data-id="element-438">
              <h2 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-439">
                Recent Listings
              </h2>
              <div className="space-y-4" data-id="element-440">
                {mockListings.slice(0, 5).map(listing => <div key={listing.id} className="flex items-center justify-between py-3 border-b border-gray-100 last:border-0" data-id="element-441">
                    <div className="flex items-center space-x-4" data-id="element-442">
                      <img src={listing.images[0]} alt={listing.title} className="w-16 h-16 rounded-lg object-cover" data-id="element-443" />
                      <div data-id="element-444">
                        <h3 className="font-medium text-gray-900" data-id="element-445">
                          {listing.title}
                        </h3>
                        <p className="text-sm text-gray-500" data-id="element-446">
                          {listing.location}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-4" data-id="element-447">
                      <span className="text-sm font-medium text-gray-900" data-id="element-448">
                        {formatCurrency(listing.price)}
                      </span>
                      {listing.status === 'approved' && <span className="flex items-center text-xs font-medium text-green-800 bg-green-100 px-2 py-1 rounded-full" data-id="element-449">
                          <CheckCircleIcon className="h-3 w-3 mr-1" data-id="element-450" />
                          Approved
                        </span>}
                      {listing.status === 'pending' && <span className="flex items-center text-xs font-medium text-yellow-800 bg-yellow-100 px-2 py-1 rounded-full" data-id="element-451">
                          <ClockIcon className="h-3 w-3 mr-1" data-id="element-452" />
                          Pending
                        </span>}
                      {listing.status === 'rejected' && <span className="flex items-center text-xs font-medium text-red-800 bg-red-100 px-2 py-1 rounded-full" data-id="element-453">
                          <XCircleIcon className="h-3 w-3 mr-1" data-id="element-454" />
                          Rejected
                        </span>}
                    </div>
                  </div>)}
              </div>
            </div>
          </div>}

        {/* Listings Tab */}
        {activeTab === 'listings' && <div className="bg-white rounded-xl shadow-sm overflow-hidden" data-id="element-455">
            <div className="p-6 border-b border-gray-200" data-id="element-456">
              <h2 className="text-lg font-semibold text-gray-900" data-id="element-457">
                All Listings
              </h2>
            </div>
            <div className="overflow-x-auto" data-id="element-458">
              <table className="min-w-full divide-y divide-gray-200" data-id="element-459">
                <thead className="bg-gray-50" data-id="element-460">
                  <tr data-id="element-461">
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-462">
                      Property
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-463">
                      Owner
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-464">
                      Price
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-465">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-466">
                      Created
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-467">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200" data-id="element-468">
                  {mockListings.map(listing => <tr key={listing.id} className="hover:bg-gray-50" data-id="element-469">
                      <td className="px-6 py-4" data-id="element-470">
                        <div className="flex items-center" data-id="element-471">
                          <img src={listing.images[0]} alt={listing.title} className="w-12 h-12 rounded-lg object-cover" data-id="element-472" />
                          <div className="ml-4" data-id="element-473">
                            <div className="text-sm font-medium text-gray-900" data-id="element-474">
                              {listing.title}
                            </div>
                            <div className="text-sm text-gray-500" data-id="element-475">
                              {listing.location}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-476">
                        <div className="text-sm text-gray-900" data-id="element-477">
                          {listing.owner.name}
                        </div>
                        <div className="text-sm text-gray-500" data-id="element-478">
                          {listing.owner.email}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-479">
                        <div className="text-sm font-medium text-gray-900" data-id="element-480">
                          {formatCurrency(listing.price)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-481">
                        {listing.status === 'approved' && <span className="flex items-center text-xs font-medium text-green-800 bg-green-100 px-2 py-1 rounded-full w-fit" data-id="element-482">
                            <CheckCircleIcon className="h-3 w-3 mr-1" data-id="element-483" />
                            Approved
                          </span>}
                        {listing.status === 'pending' && <span className="flex items-center text-xs font-medium text-yellow-800 bg-yellow-100 px-2 py-1 rounded-full w-fit" data-id="element-484">
                            <ClockIcon className="h-3 w-3 mr-1" data-id="element-485" />
                            Pending
                          </span>}
                        {listing.status === 'rejected' && <span className="flex items-center text-xs font-medium text-red-800 bg-red-100 px-2 py-1 rounded-full w-fit" data-id="element-486">
                            <XCircleIcon className="h-3 w-3 mr-1" data-id="element-487" />
                            Rejected
                          </span>}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-488">
                        {formatDate(listing.createdAt)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-489">
                        <div className="flex justify-end space-x-2" data-id="element-490">
                          <Link to={`/listing/${listing.id}`} className="text-blue-600 hover:text-blue-900" title="View" data-id="element-491">
                            <EyeIcon className="h-5 w-5" data-id="element-492" />
                          </Link>
                          {listing.status === 'pending' && <>
                              <button onClick={() => handleApproveListing(listing.id)} className="text-green-600 hover:text-green-900" title="Approve" data-id="element-493">
                                <CheckCircleIcon className="h-5 w-5" data-id="element-494" />
                              </button>
                              <button onClick={() => handleRejectListing(listing.id)} className="text-red-600 hover:text-red-900" title="Reject" data-id="element-495">
                                <XCircleIcon className="h-5 w-5" data-id="element-496" />
                              </button>
                            </>}
                          {listing.status === 'rejected' && <button className="text-yellow-600 hover:text-yellow-900" title="View rejection reason" onClick={() => alert(listing.rejectionReason)} data-id="element-497">
                              <AlertCircleIcon className="h-5 w-5" data-id="element-498" />
                            </button>}
                        </div>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
          </div>}

        {/* Users Tab */}
        {activeTab === 'users' && <div className="bg-white rounded-xl shadow-sm overflow-hidden" data-id="element-499">
            <div className="p-6 border-b border-gray-200" data-id="element-500">
              <h2 className="text-lg font-semibold text-gray-900" data-id="element-501">All Users</h2>
            </div>
            <div className="overflow-x-auto" data-id="element-502">
              <table className="min-w-full divide-y divide-gray-200" data-id="element-503">
                <thead className="bg-gray-50" data-id="element-504">
                  <tr data-id="element-505">
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-506">
                      User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-507">
                      Role
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-508">
                      License
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-509">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-510">
                      Joined
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-511">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200" data-id="element-512">
                  {mockUsers.filter(u => u.role !== 'admin').map(user => <tr key={user.id} className="hover:bg-gray-50" data-id="element-513">
                        <td className="px-6 py-4" data-id="element-514">
                          <div className="flex items-center" data-id="element-515">
                            <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center" data-id="element-516">
                              <UsersIcon className="h-5 w-5 text-gray-500" data-id="element-517" />
                            </div>
                            <div className="ml-4" data-id="element-518">
                              <div className="text-sm font-medium text-gray-900" data-id="element-519">
                                {user.name}
                              </div>
                              <div className="text-sm text-gray-500" data-id="element-520">
                                {user.email}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-521">
                          <span className="px-2 py-1 text-xs font-medium rounded-full capitalize bg-blue-100 text-blue-800" data-id="element-522">
                            {user.role}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-523">
                          {user.license ? <div className="text-sm" data-id="element-524">
                              <div className="font-medium text-gray-900 capitalize" data-id="element-525">
                                {licensePlans.find(p => p.id === user.license?.licenseId)?.planName}
                              </div>
                              <div className="text-gray-500" data-id="element-526">
                                {user.license.usedListings}/
                                {user.license.maxListings} used
                              </div>
                            </div> : <span className="text-sm text-gray-500" data-id="element-527">
                              No license
                            </span>}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap" data-id="element-528">
                          {user.isActive ? <span className="flex items-center text-xs font-medium text-green-800 bg-green-100 px-2 py-1 rounded-full w-fit" data-id="element-529">
                              <CheckCircleIcon className="h-3 w-3 mr-1" data-id="element-530" />
                              Active
                            </span> : <span className="flex items-center text-xs font-medium text-gray-600 bg-gray-100 px-2 py-1 rounded-full w-fit" data-id="element-531">
                              <XCircleIcon className="h-3 w-3 mr-1" data-id="element-532" />
                              Inactive
                            </span>}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-533">
                          {formatDate(user.createdAt)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-534">
                          <button onClick={() => handleToggleUserStatus(user.id)} className={`${user.isActive ? 'text-red-600 hover:text-red-900' : 'text-green-600 hover:text-green-900'}`} data-id="element-535">
                            {user.isActive ? 'Deactivate' : 'Activate'}
                          </button>
                        </td>
                      </tr>)}
                </tbody>
              </table>
            </div>
          </div>}

        {/* Revenue Tab */}
        {activeTab === 'revenue' && <div className="space-y-6" data-id="element-536">
            <div className="bg-white rounded-xl shadow-sm p-6" data-id="element-537">
              <h2 className="text-lg font-semibold text-gray-900 mb-6" data-id="element-538">
                Revenue Overview
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-539">
                {licensePlans.map(plan => {
              const planUsers = mockUsers.filter(u => u.license?.licenseId === plan.id);
              const planRevenue = planUsers.length * plan.price;
              return <div key={plan.id} className="border border-gray-200 rounded-lg p-6" data-id="element-540">
                      <h3 className="text-lg font-semibold text-gray-900 mb-2" data-id="element-541">
                        {plan.planName}
                      </h3>
                      <div className="text-3xl font-bold text-primary mb-4" data-id="element-542">
                        {formatCurrency(planRevenue)}
                      </div>
                      <div className="space-y-2 text-sm text-gray-600" data-id="element-543">
                        <div className="flex justify-between" data-id="element-544">
                          <span data-id="element-545">Active subscriptions:</span>
                          <span className="font-medium text-gray-900" data-id="element-546">
                            {planUsers.length}
                          </span>
                        </div>
                        <div className="flex justify-between" data-id="element-547">
                          <span data-id="element-548">Price per month:</span>
                          <span className="font-medium text-gray-900" data-id="element-549">
                            {formatCurrency(plan.price)}
                          </span>
                        </div>
                        <div className="flex justify-between" data-id="element-550">
                          <span data-id="element-551">Max listings:</span>
                          <span className="font-medium text-gray-900" data-id="element-552">
                            {plan.maxListings}
                          </span>
                        </div>
                      </div>
                    </div>;
            })}
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm p-6" data-id="element-553">
              <h2 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-554">
                Total Platform Revenue
              </h2>
              <div className="text-4xl font-bold text-primary mb-2" data-id="element-555">
                {formatCurrency(totalRevenue)}
              </div>
              <p className="text-gray-600" data-id="element-556">
                From {usersWithLicense} active license subscriptions
              </p>
            </div>
          </div>}
      </div>
    </div>;
};
export default AdminDashboardPage;